import { useEffect } from 'react'

import { useTimelineExtGapMutation } from '@/common/api/modules/article'
import { scoreTextColor } from '@/common/app/modules/media/articleCreator'
import { ExtGapType, LinkStructureAnalysis } from '@/common/model/media/articleCreator'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import DualAxisLineChart from '@/components/view/atoms/chart/DualAxisLineChart'
import FormCaption, { CaptionType } from '@/components/view/atoms/form/FormCaption'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconText from '@/components/view/atoms/icon/IconText'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import HierarchyRowList from '@/components/view/layouts/row/HierarchyRowList'
import Row from '@/components/view/layouts/row/Row'
import PriorityBar from '@/components/view/molecules/bar/PriorityBar'

import AnalysisActionList from '@/components/container/modules/media/action/AnalysisActionList'

interface Props {
  siteId: number
  historyId: number
  analysis: LinkStructureAnalysis
}

// 統計サマリー（分析ページ数・孤立ページ数・リンク不足・推奨リンク数・クラスター数・健全性スコア）
const StatsRow = ({ summary }: { summary: LinkStructureAnalysis['summary'] }) => {
  const s = summary ?? {}
  const tiles = [
    { label: '分析ページ数', value: s.total_pages ?? 0 },
    { label: '孤立ページ数', value: s.orphan_count ?? 0 },
    { label: 'リンク不足ページ', value: s.weak_count ?? 0 },
    { label: '推奨リンク数', value: s.suggested_link_count ?? 0 },
    { label: 'クラスター数', value: s.cluster_count ?? 0 },
    {
      label: '健全性スコア',
      value: s.health_score ?? 0,
      suffix: '%',
      color: scoreTextColor(s.health_score ?? 0)
    }
  ]
  return (
    <Area bottomMargin={16}>
      <SummaryItemList>
        {tiles.map((t, index) => (
          <SummaryItem key={index}>
            <Text size={12} weight='bold'>
              {t.label}
            </Text>
            <ItemAlign gapX={4} align='baseline'>
              <Text size={24} weight='bold' color={t.color}>
                {t.value}
              </Text>
              {t.suffix && (
                <Text size={14} color={TextColor.SECONDARY}>
                  {t.suffix}
                </Text>
              )}
            </ItemAlign>
          </SummaryItem>
        ))}
      </SummaryItemList>
    </Area>
  )
}

// 孤立ページ（他ページとの関連が薄い）
const OrphanPagesSection = ({
  ls
}: {
  ls: NonNullable<LinkStructureAnalysis['link_structure']>
}) => {
  const orphans = ls.orphan_pages ?? []
  if (orphans.length === 0) return null
  return (
    <Area bottomMargin={16}>
      <Heading subText='対象は本サイトの特集ページ（記事）です。分析はページID単位で紐づいており、「記事詳細」から該当記事に遷移できます。'>
        孤立ページ（他ページとの関連が薄い）
      </Heading>
      <AreaContent>
        <div className='flex flex-col gap-2'>
          {orphans.map((p, i) => (
            <InsideArea key={i} border={'emphasis'}>
              <div className='flex items-center justify-between gap-3'>
                <div>
                  <IconText
                    direction='horizon'
                    iconType={IconType.ARTICLE}
                    color={IconColor.EMPHASIS}
                    text={p.title}
                    textColor={TextColor.EMPHASIS}
                  />
                  <FormCaption type={CaptionType.CAUTION} size={12}>
                    {p.reason}
                  </FormCaption>
                </div>
                {(p.suggested_link_from?.length ?? 0) > 0 && (
                  <div className='flex flex-col items-start gap-1'>
                    <Badge size={10} badgeType={BadgeType.SQUARE_TERTIARY_SECONDARY}>
                      リンク元候補
                    </Badge>
                    <div className='flex flex-wrap gap-1'>
                      {p.suggested_link_from?.map((t, ti) => (
                        <Badge key={ti} size={10} badgeType={BadgeType.SQUARE_TERTIARY}>
                          <IconText
                            direction='horizon'
                            iconType={IconType.ARTICLE}
                            color={IconColor.SECONDARY}
                            text={t}
                            textColor={TextColor.PRIMARY}
                            textSize={10}
                            noBold
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </InsideArea>
          ))}
        </div>
      </AreaContent>
    </Area>
  )
}

// 推奨内部リンク（深刻度は重複度に相当する優先度から判定）
const SuggestedLinksSection = ({
  ls
}: {
  ls: NonNullable<LinkStructureAnalysis['link_structure']>
}) => {
  const links = ls.suggested_links ?? []
  if (links.length === 0) return null
  return (
    <Area bottomMargin={16}>
      <Heading subText='リンク元・リンク先はいずれも本サイトの特集ページ（記事）です。'>
        推奨内部リンク
      </Heading>
      <AreaContent>
        <div className='flex flex-col gap-2'>
          {links.slice(0, 15).map((l, index) => (
            <PriorityBar
              key={index}
              priority={l.priority}
              title={undefined}
              subContent={
                <ItemAlign gapX={4} gapY={4} direction='horizon' nowrap align='center'>
                  <div className='flex min-w-[600px] items-center gap-2'>
                    <InsideArea color={BgColor.SECONDARY}>
                      <IconText
                        direction='horizon'
                        iconType={IconType.ARTICLE}
                        color={IconColor.EMPHASIS}
                        text={l.from_title}
                        textColor={TextColor.EMPHASIS}
                      />
                    </InsideArea>
                    <Text size={14} color={TextColor.SECONDARY}>
                      →
                    </Text>
                    <InsideArea color={BgColor.SECONDARY}>
                      <IconText
                        direction='horizon'
                        iconType={IconType.ARTICLE}
                        color={IconColor.EMPHASIS}
                        text={l.to_title}
                        textColor={TextColor.EMPHASIS}
                      />
                    </InsideArea>
                  </div>
                  <AiInsideArea border>
                    <ItemAlign gapX={4} align='start' direction='horizon' nowrap>
                      <Icon type={IconType.AI_FILL} />
                      <Text size={12} color={TextColor.AI}>
                        アンカーテキスト案「{l.anchor_text}」： {l.reason}
                      </Text>
                    </ItemAlign>
                  </AiInsideArea>
                </ItemAlign>
              }
            />
          ))}
          <FormCaption size={12}>
            深刻度は重複度から自動判定（80%以上=高／50〜79%=中／50%未満=低）。「新規」は前回分析では未検出だったペア。{' '}
          </FormCaption>
        </div>
      </AreaContent>
    </Area>
  )
}

// トピッククラスター構造（ピラーページ・サテライトページの構成のみ表示。個別ステータス・自動生成は今回対象外）
const TopicClustersSection = ({
  ls
}: {
  ls: NonNullable<LinkStructureAnalysis['link_structure']>
}) => {
  const clusters = ls.topic_clusters ?? []
  if (clusters.length === 0) return null
  return (
    <Area bottomMargin={16}>
      <Heading subText='1つのテーマを網羅する「ピラーページ（総合解説記事）」を中心に、テーマを深掘りする「サテライトページ（専門記事）」から内部リンクを集める内部リンク戦略です。サテライトが揃うほどテーマ全体の網羅性・専門性が高まり、検索エンジンからの評価向上につながります。'>
        トピッククラスター構造
      </Heading>
      <AreaContent>
        <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
          {clusters.map((tc, i) => (
            <InsideArea key={i} color={BgColor.WHITE} border='emphasis'>
              <div className='flex flex-col gap-1.5'>
                <ItemAlign gapX={4} align='baseline'>
                  <Badge size={10} badgeType={BadgeType.KEYWORD}>
                    {tc.cluster_name}
                  </Badge>
                  <Text size={12}>クラスター</Text>
                </ItemAlign>
                <ItemAlign align='center'>
                  <Badge size={10} badgeType={BadgeType.SQUARE_PRIMARY}>
                    ピラーページ
                  </Badge>
                  {tc.pillar_page && (
                    <IconText
                      direction='horizon'
                      iconType={IconType.ARTICLE}
                      color={IconColor.EMPHASIS}
                      text={tc.pillar_page}
                      textColor={TextColor.EMPHASIS}
                    />
                  )}
                </ItemAlign>
                {((tc.satellite_pages && (tc.satellite_pages?.length ?? 0) > 0) ||
                  (tc.missing_satellites && (tc.missing_satellites?.length ?? 0) > 0)) && (
                  <HierarchyRowList type='child'>
                    {tc.satellite_pages &&
                      tc.satellite_pages.map((s, index) => (
                        <Row
                          key={index}
                          hierarchyChild
                          hierarchyLast={
                            tc.satellite_pages && index == tc.satellite_pages.length - 1
                          }
                        >
                          <div className='ml-2'>
                            <Badge size={12} badgeType={BadgeType.KEYWORD} preWrap>
                              <IconText
                                direction='horizon'
                                iconType={IconType.ARTICLE}
                                color={IconColor.PRIMARY}
                                text={s}
                                textColor={TextColor.PRIMARY}
                                noBold
                              />
                            </Badge>
                          </div>
                        </Row>
                      ))}
                    {tc.missing_satellites &&
                      tc.missing_satellites.map((message, index) => (
                        <Row
                          key={index}
                          hierarchyChild
                          hierarchyLast={
                            tc.missing_satellites && index == tc.missing_satellites.length - 1
                          }
                        >
                          <div className='ml-2 flex items-center gap-1'>
                            <Badge size={12} badgeType={BadgeType.AI} preWrap>
                              <IconText
                                direction='horizon'
                                iconType={IconType.ARTICLE}
                                color={IconColor.ASSIST}
                                text={message}
                                textColor={TextColor.AI}
                                noBold
                              />
                            </Badge>
                            <Badge badgeType={BadgeType.CAUTION_SECONDARY} size={10}>
                              未作成
                            </Badge>
                          </div>
                        </Row>
                      ))}
                  </HierarchyRowList>
                )}
                {/* {(tc.missing_satellites?.length ?? 0) > 0 && (
                  <div className='flex flex-col gap-0.5'>
                    <Text size={12} color={TextColor.CAUTION}>
                      不足サテライト（未作成）
                    </Text>
                    <HierarchyRowList type='child'>
                      {tc.missing_satellites &&
                        tc.missing_satellites.map((message, index) => (
                          <Row
                            key={index}
                            hierarchyChild
                            hierarchyLast={
                              tc.missing_satellites && index == tc.missing_satellites.length - 1
                            }
                          >
                            <div className='ml-2 flex items-center'>
                              <Badge size={12} badgeType={BadgeType.AI} preWrap>
                                <IconText
                                  direction='horizon'
                                  iconType={IconType.ARTICLE}
                                  color={IconColor.ASSIST}
                                  text={message}
                                  textColor={TextColor.AI}
                                  noBold
                                />
                              </Badge>
                              <Badge badgeType={BadgeType.CAUTION_SECONDARY} size={10}>
                                未作成
                              </Badge>
                            </div>
                          </Row>
                        ))}
                    </HierarchyRowList>
                  </div>
                )} */}
              </div>
            </InsideArea>
          ))}
        </div>
      </AreaContent>
    </Area>
  )
}

// 時系列分析（過去実行の推移）。timeline_link_structure（既存API）を利用
const TimelineSection = ({ siteId }: { siteId: number }) => {
  const { trigger: fetchTimeline, data: timeline } = useTimelineExtGapMutation(
    ExtGapType.LINK_STRUCTURE
  )

  useEffect(() => {
    fetchTimeline({ site_id: siteId }).catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId])

  if (!timeline) return null

  return (
    <>
      <SummaryHeading
        subText={
          '分析履歴が2回以上ある場合にAIが対応状況を評価（過去10回分が対象）。優先度付きアクションは①②に集約しているため、ここではトレンドとリスクのみ表示します。'
        }
      >
        時系列分析（過去実行の推移）
      </SummaryHeading>
      <Area bottomMargin={16}>
        <AreaContent>
          {!timeline.available ? (
            <Text size={12} color={TextColor.SECONDARY}>
              {timeline.message ?? '時系列分析には2回以上の分析履歴が必要です'}
            </Text>
          ) : (
            <>
              <InsideArea color={BgColor.SECONDARY}>
                <div className='h-[260px] w-full'>
                  <DualAxisLineChart
                    labels={(timeline.timeline_data ?? []).map((r) => String(r.date))}
                    countLines={[
                      {
                        title: '孤立ページ数',
                        values: (timeline.timeline_data ?? []).map(
                          (r) => Number(r.orphan_count) || 0
                        )
                      },
                      {
                        title: 'リンク不足ページ',
                        values: (timeline.timeline_data ?? []).map((r) => Number(r.weak_count) || 0)
                      }
                    ]}
                    scoreLine={{
                      title: '健全性スコア',
                      values: (timeline.timeline_data ?? []).map((r) => Number(r.health_score) || 0)
                    }}
                  />
                </div>
              </InsideArea>
              <div className='flex flex-wrap gap-3'>
                <InsideArea color={BgColor.SECONDARY} fit>
                  <div className='flex min-w-[140px] flex-col items-center gap-1'>
                    <Text size={12} weight='bold'>
                      全体トレンド
                    </Text>
                    <Text size={24} weight='bold' color={TextColor.SUCCESS}>
                      {timeline.ai_evaluation?.period_evaluation?.overall_trend ?? '-'}
                    </Text>
                  </div>
                </InsideArea>
                <InsideArea color={BgColor.SECONDARY} fit>
                  <div className='flex min-w-[140px] flex-col items-center gap-1'>
                    <Text size={12} weight='bold'>
                      対応評価スコア
                    </Text>
                    <ItemAlign gapX={4} align='baseline'>
                      <Text size={24} weight='bold'>
                        {timeline.ai_evaluation?.period_evaluation?.score ?? '-'}
                      </Text>
                      <Text size={12} color={TextColor.SECONDARY}>
                        /100
                      </Text>
                    </ItemAlign>
                  </div>
                </InsideArea>
              </div>
              {timeline.ai_evaluation?.period_evaluation?.summary && (
                <AiInsideArea border>
                  <Text size={14} color={TextColor.AI}>
                    {timeline.ai_evaluation.period_evaluation.summary}
                  </Text>
                </AiInsideArea>
              )}
            </>
          )}
        </AreaContent>
      </Area>
    </>
  )
}

// 内部リンク構造分析パネル
const LinkStructurePanel = ({ siteId, historyId, analysis }: Props) => {
  const ls = analysis.link_structure ?? {}

  return (
    <>
      <StatsRow summary={analysis.summary} />
      <AnalysisActionList siteId={siteId} mediaAnalysisId={historyId} />
      <OrphanPagesSection ls={ls} />
      <SuggestedLinksSection ls={ls} />
      <TopicClustersSection ls={ls} />
      <TimelineSection siteId={siteId} />
      {analysis.summary?.recommendation && (
        <AiInsideArea border>
          <ItemAlign gapX={4} align='start' direction='horizon' nowrap>
            <Icon type={IconType.AI_FILL} />
            <Text size={12} color={TextColor.AI}>
              {analysis.summary.recommendation}
            </Text>
          </ItemAlign>
        </AiInsideArea>
      )}
    </>
  )
}

export default LinkStructurePanel
